import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { Send, Loader2, ThumbsUp } from 'lucide-react';
import { toast } from 'react-hot-toast';
import tinycolor from 'tinycolor2';
import ChatMessage from './ChatMessage';
import surveysService from '../../services/surveysService';
import { isRTL } from '../../lib/languages';
import { getTranslation } from '../../lib/translations';

const MAX_MESSAGE_LENGTH = 500;
const COOKIE_DAYS = 30;

// The chat itself. Three modes:
// - public (default): starts or resumes a stored response and talks to the public API
// - preview: the editor's static mock conversation in the survey language, no API calls
// - live preview: the editor's "try it" mode, stateless replies from the workspace API, nothing stored
// `initialResponse` is a response that already exists, for example after a score was submitted, so the
// chat continues it instead of starting a new one.
function SurveyChat({
  survey, preview = false, live = false, workspaceId, initialResponse, page, inviteId,
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [starting, setStarting] = useState(!preview);
  const [thinking, setThinking] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [session, setSession] = useState(null);
  const [startError, setStartError] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const mounted = useRef(true);

  const design = survey.design || {};
  const languageCode = survey.settings?.language || 'en';
  const rtl = isRTL(languageCode);
  const inputBackground = design.inputBackgroundColor || '#ffffff';
  const inputTextColor = readableOn(inputBackground);
  const sendButtonColor = design.sendButtonColor || '#6366f1';
  const thankYouBackground = design.thankYouBackgroundColor || '#ffffff';
  const thankYouIconColor = design.thankYouIconColor || '#6366f1';
  const interactive = !preview || live;

  useEffect(() => () => {
    mounted.current = false;
  }, []);

  const start = useCallback(async () => {
    if(preview && !live) {
      setMessages(mockConversation(languageCode, survey.title));
      setCompleted(false);
      setStarting(false);
      return;
    }
    if(initialResponse) {
      setSession({ responseId: initialResponse._id, token: initialResponse.token });
      setMessages(initialResponse.conversation);
      setCompleted(!!initialResponse.isComplete);
      setStarting(false);
      return;
    }
    setStarting(true);
    setStartError(null);
    setCompleted(false);
    setMessages([]);
    setSession(null);
    try {
      if(live) {
        const reply = await surveysService.getPreviewReply({ workspaceId, survey, conversation: [] });
        if(!mounted.current) {
          return;
        }
        setMessages([{ role: 'assistant', content: reply.content, timestamp: new Date() }]);
        return;
      }
      const resumed = await resumeResponse(survey.publicId);
      const response = resumed || await surveysService.startPublicResponse({
        publicId: survey.publicId, page, inviteId,
      });
      if(!mounted.current) {
        return;
      }
      if(!resumed) {
        writeCookie(cookieName(survey.publicId), `${response._id}.${response.token}`);
      }
      setSession(resumed ? readSession(survey.publicId) : { responseId: response._id, token: response.token });
      setMessages(response.conversation);
      setCompleted(!!response.isComplete);
    } catch(err) {
      if(mounted.current) {
        // server messages are for the dashboard; respondents get the fixed text in their language
        setStartError(getTranslation(languageCode, /no longer accepting/i.test(err.message) ? 'surveyClosed' : 'startFailed'));
      }
    } finally {
      if(mounted.current) {
        setStarting(false);
      }
    }
  }, [
    preview, live, languageCode, survey.title, survey.publicId, survey._id, workspaceId, initialResponse, page,
    inviteId,
  ]);

  useEffect(() => {
    start();
  }, [start]);

  useEffect(() => {
    if(interactive) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, thinking, interactive]);

  useEffect(() => {
    if(interactive && !starting && !thinking && !completed) {
      inputRef.current?.focus();
    }
  }, [interactive, starting, thinking, completed]);

  const send = async (event) => {
    event.preventDefault();
    const content = input.trim();
    if(!content || thinking || completed || content.length > MAX_MESSAGE_LENGTH) {
      return;
    }
    const conversation = [...messages, { role: 'user', content, timestamp: new Date() }];
    setMessages(conversation);
    setInput('');
    setThinking(true);
    try {
      if(live) {
        const reply = await surveysService.getPreviewReply({
          workspaceId,
          survey,
          conversation: conversation.map((message) => ({ role: message.role, content: message.content })),
        });
        if(!mounted.current) {
          return;
        }
        setMessages([...conversation, { role: 'assistant', content: reply.content, timestamp: new Date() }]);
        setCompleted(!!reply.shouldComplete);
        return;
      }
      const response = await surveysService.sendPublicMessage({ ...session, content });
      if(!mounted.current) {
        return;
      }
      setMessages(response.conversation);
      setCompleted(!!response.isComplete);
    } catch(err) {
      if(mounted.current) {
        setMessages(messages);
        setInput(content);
        toast.error(err.message || getTranslation(languageCode, 'sendFailed'));
      }
    } finally {
      if(mounted.current) {
        setThinking(false);
      }
    }
  };

  if(starting) {
    return (
      <div className="flex h-[600px] items-center justify-center rounded-xl shadow-lg">
        <Loader2 className="h-6 w-6 animate-spin text-gray-600" />
      </div>
    );
  }

  if(startError) {
    return (
      <div
        className="flex h-[600px] flex-col items-center justify-center rounded-xl p-8 text-center shadow-lg"
        style={{ backgroundColor: design.chatBackgroundColor || '#f8fafc', color: design.textColor || '#374151' }}
      >
        <p className="mb-6 text-lg">{startError}</p>
        <button
          type="button"
          onClick={start}
          className="rounded-full px-6 py-3 font-medium shadow-sm hover:opacity-90"
          style={{ backgroundColor: sendButtonColor, color: readableOn(sendButtonColor) }}
        >
          {getTranslation(languageCode, 'tryAgain')}
        </button>
      </div>
    );
  }

  return (
    <div
      className="flex h-[600px] flex-col overflow-hidden rounded-xl shadow-xl"
      dir={rtl ? 'rtl' : 'ltr'}
      style={{ backgroundColor: design.chatBackgroundColor || '#f8fafc' }}
    >
      <div className="flex-1 overflow-y-auto px-4 py-6 md:px-6" style={{ scrollbarWidth: 'none' }}>
        <div className="mx-auto max-w-3xl space-y-6">
          {messages.map((message, index) => (
            <ChatMessage
              key={index}
              message={message}
              isUser={message.role === 'user'}
              design={design}
              languageCode={languageCode}
            />
          ))}
          {thinking && <ChatMessage isTyping message={{}} design={design} languageCode={languageCode} />}

          {(completed || (preview && !live)) && (
            <div className="flex w-full justify-center py-8">
              <div
                className="w-full rounded-lg border border-gray-100 p-6 text-center shadow-sm"
                style={{ backgroundColor: thankYouBackground, color: readableOn(thankYouBackground) }}
              >
                <div
                  className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full"
                  style={{ backgroundColor: thankYouIconColor, color: readableOn(thankYouIconColor) }}
                >
                  <ThumbsUp className="h-6 w-6" />
                </div>
                <h4 className="mb-2 text-lg font-semibold">{getTranslation(languageCode, 'thankYouTitle')}</h4>
                <p className="text-sm opacity-80">{getTranslation(languageCode, 'thankYouMessage')}</p>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      {!completed && (
        <form
          onSubmit={send}
          className="p-4"
          style={{ backgroundColor: design.chatBackgroundColor || '#f8fafc', borderTop: '1px solid rgba(0,0,0,0.05)' }}
        >
          <div className="mx-auto flex max-w-3xl items-center gap-3">
            <div className="relative flex-1">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(event) => setInput(event.target.value.slice(0, MAX_MESSAGE_LENGTH))}
                disabled={!interactive || thinking}
                placeholder={getTranslation(languageCode, interactive ? 'placeholder' : 'previewMode')}
                maxLength={MAX_MESSAGE_LENGTH}
                className="w-full rounded-full px-5 py-3 shadow-sm focus:outline-none disabled:cursor-not-allowed"
                style={{
                  backgroundColor: inputBackground,
                  color: inputTextColor,
                  border: `1px solid ${design.inputBorderColor || '#d1d5db'}`,
                }}
              />
              {input.length > MAX_MESSAGE_LENGTH * 0.8 && (
                <span className={`pointer-events-none absolute top-1/2 -translate-y-1/2 rounded-full bg-white/90 px-1.5 py-0.5 text-xs text-gray-500 ${rtl ? 'left-3' : 'right-3'}`}>
                  {input.length}/{MAX_MESSAGE_LENGTH}
                </span>
              )}
            </div>
            <button
              type="submit"
              disabled={!interactive || !input.trim() || thinking}
              className="flex h-12 w-12 items-center justify-center rounded-full shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ backgroundColor: sendButtonColor, color: readableOn(sendButtonColor) }}
              aria-label="Send"
            >
              {thinking ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// Helpers

function readableOn(background) {
  return tinycolor.mostReadable(background, ['#111827', '#ffffff']).toHexString();
}

function mockConversation(languageCode, title) {
  const product = title || 'Product';
  return [
    { role: 'assistant', content: getTranslation(languageCode, 'welcomeMessage', { product }), timestamp: new Date() },
    { role: 'user', content: getTranslation(languageCode, 'userResponse', { product }), timestamp: new Date() },
    { role: 'assistant', content: getTranslation(languageCode, 'aiResponse', { product }), timestamp: new Date() },
  ];
}

function cookieName(publicId) {
  return `survey_response_${publicId}`;
}

// the cookie holds "<responseId>.<token>"; the token is what lets this browser continue the conversation
function readSession(publicId) {
  if(typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${cookieName(publicId)}=`));
  if(!match) {
    return null;
  }
  const [responseId, token] = match.split('=')[1].split('.');
  return responseId && token ? { responseId, token } : null;
}

async function resumeResponse(publicId) {
  const stored = readSession(publicId);
  if(!stored) {
    return null;
  }
  return surveysService.getPublicResponse(stored).catch(() => {
    writeCookie(cookieName(publicId), '', -1);
    return null;
  });
}

// SameSite=None so the cookie also works when the page is embedded in a customer's site, which requires https
function writeCookie(name, value, days = COOKIE_DAYS) {
  if(typeof document === 'undefined') {
    return;
  }
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  const secure = window.location.protocol === 'https:' ? '; SameSite=None; Secure' : '; SameSite=Lax';
  document.cookie = `${name}=${value}; expires=${expires}; path=/${secure}`;
}

export default SurveyChat;
